/**
 * Задание stylepack/option-grid (слово владельца 21.10.2026 по снимкам
 * Dance-bit: «в оригинале 4 плашки — 2 слева, 2 справа, а у нас все 4 сверху
 * вниз. Это большая разница»). Раскладка вариантов ответа сеткой у
 * single_choice / multiple_choice / multi_step_question.
 *
 * ЗЕРКАЛО канона сервера `backend/src/ai-funnels/block-schema/option-grid.ts`:
 * перечни ниже читает с диска сторож `option-grid.ends.spec.ts` и сверяет со
 * схемой и панелью в обе стороны — форму `[...] as const` не менять, не
 * обновив его.
 *
 * Накладки отдаются объектом, который рендер спредит ПОСЛЕ прежних значений;
 * нет поля — пустой объект, разметка старой воронки та же байт в байт.
 */
import { isAllowedUrl } from '@/utils/isAllowedUrl';
import { graphemeCount } from '@/i18n/format';

export const OPTION_COLUMNS_VALUES = [1, 2, 3, 4, 5] as const;

export type OptionColumns = (typeof OPTION_COLUMNS_VALUES)[number];

/** Поля раскладки, которые читают рендеры строк вариантов. */
export interface OptionGridFields extends OptionPanelFields {
  optionColumns?: OptionColumns;
}

/** Число столбцов; нет поля или чужое значение — один столбец, как раньше. */
export function optionColumnsOf(fields: OptionGridFields): OptionColumns {
  const raw: unknown = fields.optionColumns;
  return (OPTION_COLUMNS_VALUES as readonly unknown[]).includes(raw)
    ? (raw as OptionColumns)
    : 1;
}

/**
 * Накладка на КОНТЕЙНЕР списка: сетка из N равных колонок. Промежуток —
 * прежний `gap` списка (переменная пака или свой `optionGap`), поэтому у
 * сетки он один на строки и столбцы. `minmax(0, 1fr)`, а не `1fr`: колонка
 * иначе растягивается по самому длинному слову и сетка вылезает за экран.
 */
export function optionListGridOverride(fields: OptionGridFields): {
  display?: 'grid';
  gridTemplateColumns?: string;
} & OptionPanelOverride {
  const n = optionColumnsOf(fields);
  const grid: { display?: 'grid'; gridTemplateColumns?: string } =
    n === 1
      ? {}
      : { display: 'grid', gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` };
  // Задание stylepack/option-panel: подложка — у того же контейнера.
  return { ...grid, ...optionPanelOverride(fields) };
}

/**
 * Накладка на СТРОКУ варианта в сетке: ячейка может сжаться уже своей
 * подписи, а длинное слово переносится внутри ячейки, а не распирает её.
 */
export function optionCellOverride(fields: OptionGridFields): {
  minWidth?: number;
  overflowWrap?: 'anywhere';
} {
  return optionColumnsOf(fields) === 1
    ? {}
    : { minWidth: 0, overflowWrap: 'anywhere' };
}

/* ------------------------------------------------------------------------ *
 * Коммит 2 — ЗНАЧОК У ВАРИАНТА (слово владельца 21.10.2026: «у них есть
 * эмоджи, у нас эмоджи нет»). Зеркало перечней сервера; разъезд ловит тот же
 * сторож option-grid.ends.spec.ts.
 * ------------------------------------------------------------------------ */
export const OPTION_ICON_KINDS = ['emoji', 'image'] as const;
export const OPTION_ICON_POSITIONS = ['left', 'right', 'top'] as const;
export const OPTION_TRAILING_ICONS = ['none', 'arrow', 'arrow-circle'] as const;
export const OPTION_ICON_SIZE_BOUNDS = { min: 12, max: 96 } as const;

export type OptionIconPosition = (typeof OPTION_ICON_POSITIONS)[number];
export type OptionTrailingIcon = (typeof OPTION_TRAILING_ICONS)[number];
export type OptionIcon =
  | { kind: 'emoji'; value: string }
  | { kind: 'image'; url: string };

/** Размер значка, когда автор его не задал, px. */
export const OPTION_ICON_DEFAULT_SIZE = 20;

/**
 * Признак эмодзи — ЗЕРКАЛО `EMOJI_PATTERN` сервера (option-grid.ts); сторож
 * сверяет текст образца и потолок длины, менять только парой.
 */
export const EMOJI_PATTERN = /\p{Extended_Pictographic}|\p{Regional_Indicator}|⃣/u;
export const EMOJI_MAX_LENGTH = 16;

/** Ровно один символ-эмодзи — то же правило, что у схемы сервера. */
export function isSingleEmoji(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  if (value.length === 0 || value.length > EMOJI_MAX_LENGTH) return false;
  if (value.trim() !== value) return false;
  if (graphemeCount(value) !== 1) return false;
  return EMOJI_PATTERN.test(value);
}

/**
 * Годный значок варианта или undefined. Негодный адрес (`javascript:`,
 * `data:`) и чужой вид в разметку публичной страницы не уходят.
 */
export function validOptionIcon(icon: unknown): OptionIcon | undefined {
  if (!icon || typeof icon !== 'object') return undefined;
  const raw = icon as { kind?: unknown; value?: unknown; url?: unknown };
  if (raw.kind === 'emoji' && isSingleEmoji(raw.value)) {
    return { kind: 'emoji', value: raw.value as string };
  }
  if (
    raw.kind === 'image' &&
    typeof raw.url === 'string' &&
    raw.url.trim() !== '' &&
    isAllowedUrl(raw.url)
  ) {
    return { kind: 'image', url: raw.url };
  }
  return undefined;
}

/** Поля значка у блока. */
export interface OptionIconFields {
  optionIconSize?: number;
  optionIconPosition?: OptionIconPosition;
  optionTrailingIcon?: OptionTrailingIcon;
  optionTrailingIconColor?: string;
  /** Задание stylepack/option-label-hidden: подпись только для читалки. */
  optionLabelHidden?: boolean;
}

/**
 * Задание stylepack/option-label-hidden (Blinkist: карточки только со
 * значками): скрыта ли подпись варианта глазом. Только у варианта с ГОДНЫМ
 * значком — у варианта без значка карточка иначе осталась бы пустой.
 * Зеркало поля `optionLabelHidden` схемы сервера (option-grid.ts).
 */
export function optionLabelHiddenOf(
  fields: OptionIconFields,
  icon: unknown,
): boolean {
  return fields.optionLabelHidden === true && validOptionIcon(icon) !== undefined;
}

/**
 * Приём «только для читалки»: текст в разметке и в доступном имени варианта,
 * на экране не виден и места не занимает (вне потока строки). Тот же набор
 * свойств, что у скрытых строк кабинета (AmplitudeForm, LanguageSwitcher).
 */
export const OPTION_LABEL_VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

/** Размер значка в границах; нет поля — 20 px. */
export function optionIconSizeOf(fields: OptionIconFields): number {
  const v = fields.optionIconSize;
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    return OPTION_ICON_DEFAULT_SIZE;
  }
  const b = OPTION_ICON_SIZE_BOUNDS;
  return Math.round(Math.min(b.max, Math.max(b.min, v)));
}

export function optionIconPositionOf(
  fields: OptionIconFields,
): OptionIconPosition {
  return (OPTION_ICON_POSITIONS as readonly unknown[]).includes(
    fields.optionIconPosition,
  )
    ? (fields.optionIconPosition as OptionIconPosition)
    : 'left';
}

export function optionTrailingIconOf(
  fields: OptionIconFields,
): OptionTrailingIcon {
  return (OPTION_TRAILING_ICONS as readonly unknown[]).includes(
    fields.optionTrailingIcon,
  )
    ? (fields.optionTrailingIcon as OptionTrailingIcon)
    : 'none';
}

/** Свой цвет значка справа — только строгий код цвета, иначе undefined. */
export function optionTrailingIconColorOf(
  fields: OptionIconFields,
): string | undefined {
  const raw = fields.optionTrailingIconColor?.trim();
  return raw && /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(raw)
    ? raw
    : undefined;
}

/* ------------------------------------------------------------------------ *
 * Задание stylepack/option-description — ОПИСАНИЕ У ВАРИАНТА: вторая строка
 * под подписью, меньше и бледнее (у Dance-bit «Beginner» / «I've only
 * recently started dancing»). Зеркало перечней сервера (option-grid.ts);
 * разъезд ловит тот же сторож option-grid.ends.spec.ts. Описание — только
 * показ: значение ответа по-прежнему `label`.
 * ------------------------------------------------------------------------ */
export const OPTION_DESCRIPTION_BLOCK_TYPES = [
  'single_choice',
  'multiple_choice',
] as const;
export const OPTION_DESCRIPTION_FIELDS = [
  'optionDescriptionColor',
  'optionDescriptionFontSize',
  'optionDescriptionFontWeight',
] as const;
export const OPTION_DESCRIPTION_MAX_LENGTH = 200;
export const OPTION_DESCRIPTION_SIZE_BOUNDS = { min: 8, max: 72 } as const;
export const OPTION_DESCRIPTION_WEIGHT_BOUNDS = { min: 100, max: 900 } as const;
/** Нет своего кегля — на столько px меньше кегля подписи. */
export const OPTION_DESCRIPTION_SIZE_OFFSET = 3;
/** Нет своей жирности — обычная. */
export const OPTION_DESCRIPTION_DEFAULT_WEIGHT = 400;

/** Есть ли описание у вариантов этого типа блока. */
export function hasOptionDescription(type: string): boolean {
  return (OPTION_DESCRIPTION_BLOCK_TYPES as readonly string[]).includes(type);
}

/** Описание варианта для показа; нет, пусто или не строка — undefined. */
export function optionDescriptionOf(opt: {
  description?: unknown;
}): string | undefined {
  const raw = opt.description;
  return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
}

/** Поля вида описания у блока. */
export interface OptionDescriptionFields {
  optionDescriptionColor?: string;
  optionDescriptionFontSize?: number;
  optionDescriptionFontWeight?: number;
}

const inBounds = (v: unknown, b: { min: number; max: number }): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= b.min && v <= b.max;

/**
 * Стиль строки описания. Нет своего кегля — `calc(1em - 3px)`: на 3 px
 * меньше кегля подписи, какой бы её ни задал пак или автор (описание стоит
 * внутри строки варианта). Нет своего цвета — цвет подписи (в том числе
 * контрастный у выбранного варианта) с бледностью 0.7; свой цвет — ровно он
 * и у выбранного варианта тоже. Чужое значение — умолчание, в разметку не
 * уходит.
 */
export function optionDescriptionStyle(fields: OptionDescriptionFields): {
  display: 'block';
  fontSize: number | string;
  fontWeight: number;
  color?: string;
  opacity?: number;
} {
  const ownColor = fields.optionDescriptionColor?.trim();
  const color =
    ownColor && /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(ownColor)
      ? ownColor
      : undefined;
  const size = fields.optionDescriptionFontSize;
  const weight = fields.optionDescriptionFontWeight;
  return {
    display: 'block',
    fontSize: inBounds(size, OPTION_DESCRIPTION_SIZE_BOUNDS)
      ? size
      : `calc(1em - ${OPTION_DESCRIPTION_SIZE_OFFSET}px)`,
    fontWeight:
      inBounds(weight, OPTION_DESCRIPTION_WEIGHT_BOUNDS) && weight % 100 === 0
        ? weight
        : OPTION_DESCRIPTION_DEFAULT_WEIGHT,
    ...(color ? { color } : { opacity: 0.7 }),
  };
}

/* ------------------------------------------------------------------------ *
 * Задание stylepack/option-panel — ОБЩАЯ ПОДЛОЖКА ПОД ГРУППОЙ ВАРИАНТОВ
 * (Breethe: шкала из пяти эмодзи на белой «пилюле» — фон #ffffff, скругление
 * 34, отступ по бокам 20, сверху и снизу 8). Зеркало `optionPanelShape`
 * сервера (option-grid.ts); разъезд ловит тот же сторож
 * option-grid.ends.spec.ts. Подложка — у контейнера вариантов (накладка
 * `optionListGridOverride`), промежуток между вариантами и их вид прежние.
 * ------------------------------------------------------------------------ */
export const OPTION_PANEL_FIELDS = [
  'optionPanelBg',
  'optionPanelRadius',
  'optionPanelPaddingX',
  'optionPanelPaddingY',
] as const;
export const OPTION_PANEL_RADIUS_BOUNDS = { min: 0, max: 60 } as const;
export const OPTION_PANEL_PADDING_BOUNDS = { min: 0, max: 40 } as const;

/** Поля подложки у блока. */
export interface OptionPanelFields {
  optionPanelBg?: string;
  optionPanelRadius?: number;
  optionPanelPaddingX?: number;
  optionPanelPaddingY?: number;
}

export interface OptionPanelOverride {
  backgroundColor?: string;
  borderRadius?: number;
  padding?: string;
  boxSizing?: 'border-box';
}

const panelPx = (v: unknown, b: { min: number; max: number }) =>
  typeof v === 'number' && Number.isInteger(v) && v >= b.min && v <= b.max
    ? v
    : undefined;

/**
 * Накладка подложки на контейнер вариантов. Нет годного цвета — пусто (три
 * остальных поля ни на что не действуют, разметка прежняя). Скругление вне
 * границ — без скругления, отступ вне границ — 0. `border-box`: отступы
 * внутри ширины контейнера, подложка не выходит за ширину блока.
 */
export function optionPanelOverride(
  fields: OptionPanelFields,
): OptionPanelOverride {
  const bg = fields.optionPanelBg?.trim();
  if (!bg || !/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(bg)) {
    return {};
  }
  const radius = panelPx(fields.optionPanelRadius, OPTION_PANEL_RADIUS_BOUNDS);
  const x = panelPx(fields.optionPanelPaddingX, OPTION_PANEL_PADDING_BOUNDS) ?? 0;
  const y = panelPx(fields.optionPanelPaddingY, OPTION_PANEL_PADDING_BOUNDS) ?? 0;
  return {
    backgroundColor: bg,
    ...(radius !== undefined ? { borderRadius: radius } : {}),
    padding: `${y}px ${x}px`,
    boxSizing: 'border-box',
  };
}
